
import React, { useState } from 'react';

interface ImageOptimizerProps {
  src: string;
  alt: string;
  width?: number;
  height?: number;
  className?: string;
  loading?: 'lazy' | 'eager';
  fetchPriority?: 'high' | 'low' | 'auto';
  objectFit?: 'contain' | 'cover' | 'fill' | 'none' | 'scale-down';
}

/**
 * ImageOptimizer component for optimized image loading
 * 
 * This component:
 * - Uses proper loading attributes (lazy or eager)
 * - Sets correct width and height to prevent layout shifts
 * - Supports fetch priority for critical images
 * - Uses fade-in effect for better user experience
 */
export const ImageOptimizer: React.FC<ImageOptimizerProps> = ({
  src,
  alt,
  width,
  height,
  className = "",
  loading = "lazy",
  fetchPriority = "auto",
  objectFit = "contain"
}) => {
  const [isLoaded, setIsLoaded] = useState(false);
  
  // Generate a srcset for responsive images when possible
  const generateSrcSet = () => {
    // Only generate srcsets for our lovable uploads
    if (!src.includes('lovable-uploads')) return undefined;
    
    return `${src} 1x, ${src} 2x`;
  };

  return (
    <img
      src={src}
      alt={alt}
      width={width}
      height={height}
      loading={loading}
      fetchPriority={fetchPriority}
      className={`transition-opacity duration-300 ${isLoaded ? 'opacity-100' : 'opacity-0'} ${className}`}
      onLoad={() => setIsLoaded(true)}
      onError={(e) => {
        const imgElement = e.currentTarget;
        imgElement.onerror = null;
        imgElement.src = '/placeholder.svg';
      }}
      style={{ objectFit }}
      decoding="async"
      srcSet={generateSrcSet()}
    />
  );
};

export default ImageOptimizer;
